import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const mainLinks = [
  { label: "Archived Data", to: "/CcpAdminArchivedData" },
  { label: "Course Planning", to: "/CcpAdminCoursePlanning" },
  { label: "Districts", to: "/CcpAdminDistricts" },
  { label: "Portal", sub: true },
  { label: "Schools", to: "/CcpAdminSchools" },
  { label: "School View", to: "/CcpAdminSchoolView" },
  { label: "Users", sub: true },
];

const userLinks = [
  { label: "Admin Users", to: "/Admin-users" },
  { label: "School Users", to: "/SchoolUsers" },
];

const portalLinks = [
  { label: "Announcements", to: "/CcpAdminAnnouncements" },
  { label: "Course Change Request", to: "/CcpAdminChangeCourseRequest" },
  { label: "FAQs", to: "/Faqs" },
  { label: "Forms", to: "/CcpAdminForms" },
  { label: "New Course Request", to: "/CcpAdminNewCourseRequest" },
  { label: "Resources", to: "/CcpAdminResources" },
  { label: "School Feedback", to: "/CcpAdminSchoolFeedback" },
  { label: "Terms", to: "/CcpAdminTerm" },
];

// Sort all links alphabetically by label
mainLinks.sort((a, b) => a.label.localeCompare(b.label));
userLinks.sort((a, b) => a.label.localeCompare(b.label));
portalLinks.sort((a, b) => a.label.localeCompare(b.label));

const CcpAdminBreadCrumbs = ({ pageName }) => {
  const location = useLocation();
  const [openSub, setOpenSub] = useState(null); // 'users' or 'portal' or null

  // Find the active link label based on the current path
  const allLinks = [
    ...mainLinks.filter((l) => !l.sub),
    ...userLinks,
    ...portalLinks,
  ];
  const activeLink = allLinks.find((item) => location.pathname === item.to);
  const displayName =
    pageName || (activeLink ? activeLink.label : "Admin Panel");

  // Helper to render a link or a sub-menu trigger
  const renderLink = (item, idx, arr) => {
    if (item.sub) {
      const isOpen = openSub === item.label.toLowerCase();
      return (
        <React.Fragment key={item.label}>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              textDecoration: "none",
              color: "#AC1A2F",
              background: isOpen ? "#f0e6e8" : "transparent",
              fontWeight: 700,
              fontSize: "0.95rem",
              cursor: "pointer",
              transition: "background 0.2s, color 0.2s",
              display: "flex",
              alignItems: "center",
              mr: idx < arr.length - 1 ? 0.5 : 0,
              mb: 0.5,
              minWidth: "80px",
              justifyContent: "center",
              border: isOpen ? "1px solid #AC1A2F" : "1px solid transparent",
            }}
            onClick={() => setOpenSub(isOpen ? null : item.label.toLowerCase())}
          >
            {item.label}
          </Box>
          {idx < arr.length - 1 && (
            <ChevronRightIcon sx={{ color: "#bbb", mx: 0.5, fontSize: 18 }} />
          )}
        </React.Fragment>
      );
    } else {
      const isActive = location.pathname === item.to;
      return (
        <React.Fragment key={item.to}>
          <Box
            component={Link}
            to={item.to}
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              textDecoration: "none",
              color: isActive ? "#fff" : "#AC1A2F",
              background: isActive ? "#AC1A2F" : "transparent",
              fontWeight: isActive ? 700 : 500,
              fontSize: "0.95rem",
              transition: "background 0.2s, color 0.2s",
              "&:hover": {
                background: isActive ? "#AC1A2F" : "#e0e0e0",
                color: isActive ? "#fff" : "#AC1A2F",
              },
              display: "flex",
              alignItems: "center",
              mr: idx < arr.length - 1 ? 0.5 : 0,
              mb: 0.5,
              minWidth: "80px",
              justifyContent: "center",
            }}
          >
            {item.label}
          </Box>
          {idx < arr.length - 1 && (
            <ChevronRightIcon sx={{ color: "#bbb", mx: 0.5, fontSize: 18 }} />
          )}
        </React.Fragment>
      );
    }
  };

  // Render sub-links if a sub is open
  const renderSubLinks = (sub) => {
    let links = [];
    if (sub === "users") links = userLinks;
    if (sub === "portal") links = portalLinks;
    return (
      <Box
        sx={{
          width: "100%",
          background: "#f5f5f5",
          py: 0.5,
          px: 2,
          borderRadius: 3,
          boxShadow: 1,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          mb: 2,
          mt: 1,
        }}
      >
        {links.map((item, idx) => renderLink(item, idx, links))}
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 2 }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ color: "#222", textAlign: "center", mb: 1 }}
        >
          {displayName}
        </Typography>
      </Box>
      <Box
        sx={{
          width: "100%",
          background: "#f5f5f5",
          py: 0.5,
          px: 2,
          borderRadius: 3,
          boxShadow: 1,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          mb: 2,
          overflowX: "auto",
        }}
      >
        {mainLinks.map((item, idx) => renderLink(item, idx, mainLinks))}
      </Box>
      {openSub && renderSubLinks(openSub)}
    </>
  );
};

export default CcpAdminBreadCrumbs;
